import { useEffect, useRef, useState } from 'react'
import {
    hasCustomers, createInvoice, getInvoice, updateInvoice, finalizeInvoice, invoicePdfUrl, invoicePdfTabId,
    getRefundPreview, refundInvoice, sendInvoice, getSendPreview, type MailOverrides, getTaxDecision, cancelInvoice, reissueInvoice, duplicateInvoice, NO_PSP_SOURCES,
    changeInvoiceLineText, textChangePdfUrl, type InvoiceTextChange,
    type LineItem, type TaxDecision, type RefundPreview, type RefundReason, type InvoiceDetail,
} from '../api'
import { useTabs, useDataVersion, useDataVersionUnlessDirty, useDirtyGuard, type SaveResult } from '../layout/TabsContext'
import { useAuth, useCurrency } from '../layout/AuthContext'
import { useToast } from '../layout/ToastContext'
import InfoTip from '../components/InfoTip'
import ConfirmDialog from '../components/ConfirmDialog'
import SendMailDrawer from '../components/SendMailDrawer'
import { BRANDS, moneySign, invoiceStatusLabel, invoiceTabName } from '../lookups'
import { fill, textOf, TAX_DECISION_REASONS, INVOICE_DRAFT_STALE, STALE_RELOAD } from '../../shared/texts'
import { vatZeichen, type VatStand } from '../vatMark'
import LineItemsEditor from '../components/LineItemsEditor'
import CustomerPicker from '../components/CustomerPicker'
import { checkBeforeSave } from '../forms/checkForm'
import { DEFAULT_LINE_TAX_RATE, MONEY_EPSILON, DEFAULT_FORM_BRAND } from '../../shared/constants'
import { PAGES_INVOICE_FORM_TEXTS as T } from '../../shared/texts'

// PSP payment states in plain German — the raw values come from the transaction mirror.
const PAYMENT_STATUS: Record<string, string> = T.paymentStatus

export default function InvoiceForm({ tabId, id }: { tabId?: string, id?: string }) {
    const formRef = useRef<HTMLFormElement>(null)
    const { publishChange, closeTab, openTab, renameTab } = useTabs()
    const { can, user } = useAuth()
    // Changing the text of an issued invoice is the ROLE admin's alone (server: routes/invoices.ts).
    const isAdmin = user?.role === 'admin'
    const { toast } = useToast()
    const canWrite = can('invoices', 'write')
    // null while unknown — so the "create one first" notice doesn't flash up during loading.
    const [anyCustomer, setAnyCustomer] = useState<boolean | null>(null)
    const customersVersion = useDataVersion('customers')
    const [error, setError] = useState('')
    // A draft's updatedAt as loaded — sent with the save, refused by the server if it moved
    // (the form has no edit lock, so two people can sit on one draft).
    const [loadedAt, setLoadedAt] = useState<string | undefined>()
    const [stale, setStale] = useState(false)
    const [busy, setBusy] = useState<string | null>(null)
    const [loading, setLoading] = useState(!!id)
    const [status, setStatus] = useState<string>('') // existing invoice status (empty for new)
    const [invoiceNumber, setInvoiceNumber] = useState<string | undefined>()
    const [pdfName, setPdfName] = useState<string | undefined>()
    // A credit note reverses another invoice — refunding it is meaningless, so no button.
    const [isCreditNote, setIsCreditNote] = useState(false)
    // PSP side of the document (paid / refunded / which payment) — null if there is none.
    const [payment, setPayment] = useState<InvoiceDetail['payment']>(null)
    // Refund state of THIS document. Kept in the form so a tab that stays open after a refund
    // locks the button — otherwise the second click only fails at the server.
    const [refundedAmount, setRefundedAmount] = useState(0)
    const [chargeback, setChargeback] = useState<{ at: string, amount: number, reversedAt?: string } | null>(null)
    const [refundPending, setRefundPending] = useState(false)
    // No PSP behind the invoice (manual, abo run) → it is cancelled, not refunded.
    const [source, setSource] = useState('')
    const [credited, setCredited] = useState(0)
    const [confirmCancel, setConfirmCancel] = useState(false)
    // Text of an issued invoice (admin, 03.11.2026, 62b/63a/64b).
    const [legacy, setLegacy] = useState(false)
    const [textChanges, setTextChanges] = useState<InvoiceTextChange[]>([])
    const [textEdit, setTextEdit] = useState<{ lineIndex: number, text: string, reason: string } | null>(null)
    const noPsp = NO_PSP_SOURCES.includes(source)
    const cancelled = credited > MONEY_EPSILON

    const [customerId, setCustomerId] = useState('')
    const [brand, setBrand] = useState(DEFAULT_FORM_BRAND)
    // Display only: the invoice's own currency, a new one is issued in the backoffice currency.
    const appCurrency = useCurrency()
    const [storedCurrency, setStoredCurrency] = useState('')
    const currency = storedCurrency || appCurrency
    const sign = moneySign(currency) // the refund dialog speaks the invoice's currency
    const [periodLabel, setPeriodLabel] = useState('')
    const [items, setItems] = useState<LineItem[]>([{ description: '', quantity: 1, unitPrice: 0, taxRate: DEFAULT_LINE_TAX_RATE }])
    const [baseline, setBaseline] = useState<string | null>(null)
    const [confirmFinalize, setConfirmFinalize] = useState(false)
    // Refund dialog: preview is loaded on open, feeInput lets the operator override the
    // configured fee for this one case.
    const [refund, setRefund] = useState<RefundPreview | null>(null)
    const [refundOpen, setRefundOpen] = useState(false)
    const [feeInput, setFeeInput] = useState('')
    const [refundDone, setRefundDone] = useState('')
    // Statutory withdrawal forbids keeping a fee — the choice drives the form, the server
    // enforces it regardless.
    const [refundReason, setRefundReason] = useState<RefundReason>('cancellation')

    // Every message goes to a toast as well: the inline notice sits at the bottom of a long
    // form, so on anything but a short invoice it is simply not seen.
    function fail(msg: string) { setError(msg); toast(msg, 'err') }
    function done(msg: string) { setRefundDone(msg); toast(msg, 'ok') }

    // Lock the refund button once one is under way or done. The server refuses a second refund
    // either way, but a live button that only fails on click invites exactly that attempt —
    // and between trigger and the PSP callback there is a window of minutes.
    // An open chargeback blocks it too: the bank already took the money back, a refund would
    // pay the customer twice (and the PSP refuses one while the dispute is open).
    const chargedBackOpen = !!chargeback && !chargeback.reversedAt
    const refundBlocked = refundPending || refundedAmount > MONEY_EPSILON || chargedBackOpen
    const refundBlockReason = refundPending
        ? T.refundPending
        : chargedBackOpen
            ? T.refundChargedBack
        : refundedAmount > MONEY_EPSILON
            ? fill(T.refundAlreadyDone, { amount: refundedAmount.toLocaleString('de-DE', { minimumFractionDigits: 2 }), currency })
            : ''

    // Distinct tax rates across the positions — see finalizeInvoice: a document can only state
    // one, so a mixed invoice is refused server-side.
    const mixedRates = [...new Set(items.map((i) => Number(i.taxRate ?? 0)))].sort((a, b) => a - b)


    // Existing finalized/sent/void invoices are immutable (Art. 244 ff. MwStSystRL; "GoBD"
    // is the house shorthand, see services/invoices.ts) → view-only. New or
    // draft invoices are editable (with write permission).
    const editable = canWrite && (!id || status === 'draft')
    const viewOnly = !editable

    // Reverse charge is decided by the rule on the server (services/vat.ts), the same one the abo
    // run uses; finalize enforces it. Shown here so the refusal is never a surprise. A customer
    // the operator PICKS takes the decision's rate into every line; rates set by hand afterwards
    // stay until the customer changes — finalize refuses a contradiction instead of rewriting.
    const [taxDecision, setTaxDecision] = useState<TaxDecision | null>(null)
    const pickedCustomer = useRef(false)
    const pickCustomer = (cid: string) => { pickedCustomer.current = true; setCustomerId(cid) }
    const decided = !!taxDecision && taxDecision.code !== 'unreachable'
    const rateContradiction = decided && (taxDecision!.reverseCharge
        ? items.some((i) => Number(i.taxRate) !== 0)
        : items.some((i) => Number(i.taxRate) === 0))
    const applyDecisionRate = () => {
        if (decided) setItems((prev) => prev.map((i) => ({ ...i, taxRate: taxDecision!.taxPercent })))
    }
    // The sign from vatMark; the sentence is the decision's own reason.
    const decisionStand: VatStand | null = taxDecision && ({
        ok: { vatId: taxDecision.vatId, vatIdValid: true },
        invalid: { vatId: taxDecision.vatId, vatIdValid: false },
        country_mismatch: { vatId: taxDecision.vatId, vatIdValid: false },
        format: { vatId: taxDecision.vatId },
        domestic: { vatId: taxDecision.vatId, taxCase: 'domestic-cy' },
        unreachable: { vatId: taxDecision.vatId },
    } as Record<string, VatStand>)[taxDecision.code] || {}
    useEffect(() => {
        if (!customerId || !editable) { setTaxDecision(null); return }
        let live = true
        getTaxDecision(customerId).then((d) => {
            if (!live) return
            setTaxDecision(d)
            if (pickedCustomer.current && d.code !== 'unreachable') {
                setItems((prev) => prev.map((i) => ({ ...i, taxRate: d.taxPercent })))
            }
            pickedCustomer.current = false
        }).catch(() => { if (live) setTaxDecision(null) })
        return () => { live = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    // customersVersion: a customer edited in another tab (VAT id, country) is decided anew here;
    // the address itself is taken from the customer again at finalize (finalizeInvoice).
    }, [customerId, editable, customersVersion])

    const snapshot = JSON.stringify({ customerId, brand, periodLabel, items })
    const dirty = baseline !== null && snapshot !== baseline
    // Changes to this invoice made elsewhere: cancelled or sent from another tab or the list, a
    // PSP confirming a refund (the credit note follows seconds later) or a chargeback. A credit
    // note is a different document, so the record id would not catch it — reload on any invoice
    // change, unless a draft has unsaved edits.
    const invoicesVersion = useDataVersionUnlessDirty('invoices', dirty)

    // Re-asked on every customer change, so creating the first customer in another tab unlocks
    // the form without a reload.
    useEffect(() => {
        hasCustomers().then(setAnyCustomer).catch((e) => fail(e?.message ?? T.loadFailed))
    }, [customersVersion])

    // Populate the form from a loaded invoice (also resets the clean baseline).
    function applyInvoice(inv: Awaited<ReturnType<typeof getInvoice>>) {
        setLoadedAt(inv.updatedAt); setStale(false)
        setStatus(inv.status); setInvoiceNumber(inv.invoiceNumber); setPdfName(inv.pdfName)
        // The tab was named when it was opened; finalize gives the number only now.
        if (tabId) renameTab(tabId, invoiceTabName(inv))
        setIsCreditNote(!!inv.creditNoteFor)
        setPayment(inv.payment ?? null)
        setRefundedAmount(inv.refundedAmount ?? 0)
        setChargeback(inv.chargedBackAt ? { at: inv.chargedBackAt, amount: inv.chargedBackAmount ?? 0, reversedAt: inv.chargebackReversedAt } : null)
        setRefundPending(!!inv.refundIntent)
        setSource(inv.source ?? ''); setCredited(inv.credited ?? 0)
        setLegacy(!!inv.legacy); setTextChanges(inv.textChanges ?? [])
        setCustomerId(String(inv.customerId)); setBrand(inv.brand)
        setStoredCurrency(inv.currency || '')
        setPeriodLabel(inv.periodLabel ?? '')
        const its = (inv.lineItems ?? []).map((li) => ({
            description: li.description, quantity: li.quantity, unitPrice: li.unitPrice, taxRate: li.taxRate,
        }))
        const loadedItems = its.length ? its : [{ description: '', quantity: 1, unitPrice: 0, taxRate: DEFAULT_LINE_TAX_RATE }]
        setItems(loadedItems)
        setBaseline(JSON.stringify({ customerId: String(inv.customerId), brand: inv.brand, periodLabel: inv.periodLabel ?? '', items: loadedItems }))
    }

    function loadInvoice(invId: string) {
        return getInvoice(invId).then(applyInvoice)
            .catch((e) => fail(e?.response?.data?.error ?? e?.message ?? T.invoiceLoadFailed))
    }

    // New: capture the initial defaults as the clean baseline. Edit: load the invoice.
    useEffect(() => {
        if (!id) { setBaseline(JSON.stringify({ customerId: '', brand: DEFAULT_FORM_BRAND, periodLabel: '', items: [{ description: '', quantity: 1, unitPrice: 0, taxRate: DEFAULT_LINE_TAX_RATE }] })); return }
        loadInvoice(id).finally(() => setLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, invoicesVersion])

    // Persist whatever is dirty. New → create + close; edit → update in place.
    async function save(): Promise<SaveResult> {
        // Required fields and formats from the markup — one check for every form (forms/checkForm.ts).
        const invalid = checkBeforeSave(formRef.current, setError)
        if (invalid) return invalid
        setBusy('save'); setError(''); setStale(false)
        try {
            if (id) {
                const saved = await updateInvoice(id, { brand, customerId, lineItems: items, periodLabel: periodLabel || undefined, expectedUpdatedAt: loadedAt })
                setLoadedAt(saved.updatedAt) // a second save before any reload must not look stale
                setBaseline(snapshot) // mark clean, keep the tab open
                publishChange('invoices')
            } else {
                await createInvoice({ brand, customerId, lineItems: items, periodLabel: periodLabel || undefined })
                setBaseline(snapshot)
                publishChange('invoices')
                closeTab('invoice-new')
            }
            return { ok: true }
        } catch (e: any) {
            // Changed elsewhere since loading: nothing was saved, the input stays as it is.
            const isStale = e?.response?.status === 409 && e?.response?.data?.code === 'stale'
            if (isStale) setStale(true)
            const msg = isStale ? INVOICE_DRAFT_STALE : e?.response?.data?.error ?? e?.message ?? T.saveFailed
            fail(msg)
            return { ok: false, error: msg }
        } finally { setBusy(null) }
    }

    function onSubmit(e: React.FormEvent) { e.preventDefault(); void save() }
    useDirtyGuard(tabId, dirty && editable, save)

    // Finalize the draft (assigns the invoice number; irreversible). Reloads the
    // invoice afterwards so the form flips to its read-only finalized state.
    async function doFinalize() {
        if (!id) return
        setBusy('finalize'); setError('')
        try {
            await finalizeInvoice(id)
            await loadInvoice(id)
            publishChange('invoices')
            setConfirmFinalize(false)
        } catch (e: any) { fail(e?.response?.data?.error ?? e?.message ?? T.finalizeFailed) }
        finally { setBusy(null) }
    }

    // Cancel: a credit note over the full amount, no money moved (server: cancelInvoice).
    async function doCancel() {
        if (!id) return
        setBusy('cancel'); setError('')
        try {
            const res = await cancelInvoice(id)
            await loadInvoice(id)
            publishChange('invoices')
            setConfirmCancel(false)
            toast(fill(T.cancelCreated, { invoiceNumber: res.invoiceNumber }), 'ok')
        } catch (e: any) { fail(e?.response?.data?.error ?? e?.message ?? T.cancelFailed) }
        finally { setBusy(null) }
    }

    // Change one position's text of the issued invoice; the server re-renders PDF + e-invoice.
    async function doChangeText() {
        if (!id || !textEdit) return
        setBusy('line-text'); setError('')
        try {
            await changeInvoiceLineText(id, textEdit)
            await loadInvoice(id)
            publishChange('invoices')
            setTextEdit(null)
            toast(T.lineTextDone, 'ok')
        } catch (e: any) { fail(e?.response?.data?.error ?? e?.message ?? T.lineTextFailed) }
        finally { setBusy(null) }
    }
    const issued = status === 'finalized' || status === 'sent'
    const canChangeText = isAdmin && !!id && issued && !isCreditNote && !cancelled && !legacy

    // A new draft with exactly this invoice's lines, opened in its own tab.
    async function doReissue() {
        if (!id) return
        setBusy('reissue'); setError('')
        try {
            const draft = await reissueInvoice(id)
            publishChange('invoices')
            openTab({ id: `invoice-${draft._id}`, name: invoiceTabName(draft), component: 'invoice-form', config: { id: draft._id } })
        } catch (e: any) { fail(e?.response?.data?.error ?? e?.message ?? T.reissueFailed) }
        finally { setBusy(null) }
    }

    // „Rechnung duplizieren": a new draft from this invoice, opened in its own tab (operator, 31.10.2026).
    async function doDuplicate() {
        if (!id) return
        setBusy('duplicate'); setError('')
        try {
            const draft = await duplicateInvoice(id)
            publishChange('invoices')
            openTab({ id: `invoice-${draft._id}`, name: invoiceTabName(draft), component: 'invoice-form', config: { id: draft._id } })
        } catch (e: any) { fail(e?.response?.data?.error ?? e?.message ?? T.duplicateFailed) }
        finally { setBusy(null) }
    }

    // Send (or re-send) this document. Works regardless of status — that is the point.
    // Opened by „Versenden": the send drawer shows the filled template and sends from there.
    // Errors go back to the drawer, which shows them and stays open.
    const [sendOpen, setSendOpen] = useState(false)
    async function doSend(overrides: MailOverrides) {
        if (!id) return
        setBusy('send'); setError(''); setRefundDone('')
        try {
            const res = await sendInvoice(id, overrides.invoice ?? overrides.creditNote)
            setSendOpen(false)
            await loadInvoice(id)
            publishChange('invoices')
            done(res.redirectedTo
                ? fill(T.sentRedirected, { redirectedTo: res.redirectedTo, to: res.to ?? T.sentRedirectedFallback })
                : fill(T.sentTo, { to: res.to ?? T.sentToFallback }))
        } finally { setBusy(null) }
    }

    // Load what a refund would look like, then show the dialog.
    async function openRefund() {
        if (!id) return
        setBusy('refund-preview'); setError(''); setRefundDone('')
        try {
            const p = await getRefundPreview(id)
            if (p.blocker) { fail(p.blocker); return }
            setRefund(p)
            setRefundReason('cancellation')
            setFeeInput(p.fee.fee.toFixed(2))
            setRefundOpen(true)
        } catch (e: any) { fail(e?.response?.data?.error ?? e?.message ?? T.previewFailed) }
        finally { setBusy(null) }
    }

    // Trigger it. The credit note and the access revoke follow from the PSP webhook seconds
    // later — that is why the confirmation talks about what was *requested*.
    async function doRefund() {
        if (!id || !refund) return
        setBusy('refund'); setError('')
        try {
            const fee = Number(feeInput.replace(',', '.'))
            const res = await refundInvoice(id, {
                mode: 'full',
                reason: refundReason,
                fee: refundReason === 'withdrawal' ? 0 : (Number.isFinite(fee) ? fee : undefined),
            })
            setRefundOpen(false)
            await loadInvoice(id) // the tab stays open → refresh, or the button stays armed
            done(fill(T.refundDone, { amount: res.amount.toFixed(2), fee: res.fee.toFixed(2), sign }))
            publishChange('invoices')
        } catch (e: any) { fail(e?.response?.data?.error ?? e?.message ?? T.refundFailed) }
        finally { setBusy(null) }
    }

    // What the operator would actually pay back with the fee currently in the input.
    // On a statutory withdrawal no fee may be kept, so the payout is always the full amount.
    const isWithdrawal = refundReason === 'withdrawal'
    const feeNow = isWithdrawal ? 0 : Number((feeInput || '0').replace(',', '.'))
    const payoutNow = refund ? Math.max(refund.refundable - (Number.isFinite(feeNow) ? feeNow : 0), 0) : 0

    const title = !id ? T.titleNew
        : editable ? T.titleEdit
            : fill(T.titleView, { invoiceNumber: invoiceNumber ?? T.titleViewNoNumber })

    if (loading) return <div className="form-page"><p className="muted" style={{ padding: 24 }}>{T.loading}</p></div>

    return (
        <form ref={formRef} noValidate onSubmit={onSubmit} className="form-page">
            <div className="form-top">
            <div className="form-header">
                <div className="page-title">
                    <h1>{title}</h1>
                    <InfoTip>{id ? T.infoEdit : T.infoNew}</InfoTip>
                </div>
                <div className="row">
                    {/* No copy here: „Aus letzter klonen" was removed (operator, 31.10.2026, 42) — „Rechnung
                        duplizieren" at an existing invoice and in the list does it. */}
                    {editable && !id && (
                        <button type="submit" disabled={busy !== null || !dirty || anyCustomer !== true}>
                            {busy === 'save' ? '…' : T.createDraft}
                        </button>
                    )}
                    {editable && id && (
                        <>
                            <button type="submit" disabled={busy !== null || !dirty}>{busy === 'save' ? '…' : T.save}</button>
                            <button type="button" disabled={busy !== null || dirty}
                                title={dirty ? T.saveFirst : T.finalizeHint}
                                onClick={() => setConfirmFinalize(true)}>
                                {busy === 'finalize' ? '…' : T.finalize}
                            </button>
                        </>
                    )}
                    {viewOnly && id && (status === 'finalized' || status === 'sent') && (
                        <button type="button" className="secondary"
                            onClick={() => openTab({
                                id: invoicePdfTabId(id),
                                name: pdfName ?? (invoiceNumber ? fill(T.pdfTabName, { invoiceNumber }) : T.pdfTabFallback),
                                component: 'file-viewer',
                                config: { url: invoicePdfUrl(id, pdfName), fname: pdfName ?? fill(T.pdfFileName, { name: invoiceNumber ?? id }) },
                            })}>{T.openPdf}</button>
                    )}
                    {canChangeText && (
                        <button type="button" className="secondary" disabled={busy !== null} title={T.lineTextEditHint}
                            onClick={() => { setError(''); setTextEdit({ lineIndex: 0, text: items[0]?.description ?? '', reason: '' }) }}>
                            {busy === 'line-text' ? '…' : T.lineTextEdit}
                        </button>
                    )}
                    {canWrite && id && (status === 'finalized' || status === 'sent') && (
                        <button type="button" className="secondary" disabled={busy !== null} onClick={() => setSendOpen(true)}
                            title={status === 'sent'
                                ? T.resendHint
                                : T.sendHint}>
                            {busy === 'send' ? '…' : status === 'sent' ? T.resend : T.send}
                        </button>
                    )}
                    {canWrite && id && !isCreditNote && noPsp && (status === 'finalized' || status === 'sent') && (
                        <button type="button" className="secondary"
                            disabled={busy !== null || cancelled}
                            onClick={() => setConfirmCancel(true)}
                            title={cancelled ? T.alreadyCancelled : T.cancelHint}>
                            {busy === 'cancel' ? '…' : cancelled ? T.cancelled : T.cancel}
                        </button>
                    )}
                    {/* Every status, but not a credit note and not a cancelled invoice — that one has „Neu
                        ausstellen" (operator, 31.10.2026, 39a/40). */}
                    {canWrite && id && !isCreditNote && !cancelled && (
                        <button type="button" className="secondary" disabled={busy !== null || (editable && dirty)} onClick={doDuplicate}
                            title={editable && dirty ? T.duplicateSaveFirst : T.duplicateHint}>
                            {busy === 'duplicate' ? '…' : T.duplicate}
                        </button>
                    )}
                    {canWrite && id && !isCreditNote && cancelled && noPsp && (status === 'finalized' || status === 'sent') && (
                        <button type="button" className="secondary" disabled={busy !== null} onClick={doReissue}
                            title={T.reissueHint}>
                            {busy === 'reissue' ? '…' : T.reissue}
                        </button>
                    )}
                    {canWrite && id && !isCreditNote && !noPsp && (status === 'finalized' || status === 'sent') && (
                        <button type="button" className="secondary"
                            disabled={busy !== null || refundBlocked}
                            onClick={openRefund}
                            title={refundBlockReason || T.refundHint}>
                            {busy === 'refund-preview' ? '…' : refundBlocked ? T.refunded : T.refund}
                        </button>
                    )}
                </div>
            </div>

            {/* ⚠️ Hier ist der Status ein SATZ, kein Badge — das Label muss hineinpassen, nicht
                nur das Wort ersetzt werden. Kleingeschrieben, weil „ist finalisiert/versendet/
                storniert" ein Partizip verlangt; genau diese drei sind die einzigen Zustände,
                die diese Meldung erreichen (nur unveränderliche Rechnungen sind `viewOnly`).
                Bekäme ein Status einmal ein SUBSTANTIV als Label („Entwurf"), passte der Satz
                nicht mehr und bräuchte eine eigene Formulierung. */}
            {viewOnly && id && (
                <div className="notice">{canWrite
                    ? fill(T.viewOnlyStatus, { statusLabel: invoiceStatusLabel(status).toLowerCase() })
                    : T.readOnly}</div>
            )}
            {/* The money side. The status above is the DOCUMENT's — this one is the payment's,
                and it is the one that matters before refunding. */}
            {id && payment && (
                <div className="notice">
                    <b>{fill(T.paymentHead, { provider: payment.provider })}</b> {PAYMENT_STATUS[payment.status] ?? payment.status}
                    {' · '}{payment.amount.toLocaleString('de-DE', { minimumFractionDigits: 2 })} {payment.currency}
                    {payment.amountRefunded > 0 && (
                        <>{T.paymentRefunded}<b>{payment.amountRefunded.toLocaleString('de-DE', { minimumFractionDigits: 2 })} {payment.currency}</b></>
                    )}
                    {payment.occurredAt && ` · ${new Date(payment.occurredAt).toLocaleDateString('de-DE')}`}
                    {chargeback && (
                        <> · <b style={{ color: chargeback.reversedAt ? undefined : 'var(--danger)' }}>
                            {fill(T.chargedBackOn, { date: new Date(chargeback.at).toLocaleDateString('de-DE'), amount: chargeback.amount.toLocaleString('de-DE', { minimumFractionDigits: 2 }), currency: payment.currency })}
                        </b>{chargeback.reversedAt
                            ? fill(T.chargebackReversed, { date: new Date(chargeback.reversedAt).toLocaleDateString('de-DE') })
                            : T.chargebackOpen}</>
                    )}
                    {' · '}<code>{payment.providerPaymentId}</code>
                </div>
            )}
            {id && !payment && (status === 'finalized' || status === 'sent') && (
                <div className="notice">{T.noPaymentData}</div>
            )}
            {viewOnly && !id && <div className="notice">{T.readOnly}</div>}
            {error && (
                <div className="notice err">
                    {error}
                    {stale && id && <> <button type="button" className="link-btn" onClick={() => { setError(''); void loadInvoice(id) }}>{STALE_RELOAD}</button></>}
                </div>
            )}
            {!id && anyCustomer === false && (
                <div className="notice err">{T.noCustomers}</div>
            )}
            </div>

            <div className="form-body">
            <fieldset disabled={viewOnly} style={{ border: 0, padding: 0, margin: 0 }}>
            <div className="panel">
                <h2>{T.basics}</h2>
                <div className="row" style={{ alignItems: 'flex-start' }}>
                    <div className="field field-wide"><label>{T.customer}</label>
                        <CustomerPicker value={customerId} onChange={pickCustomer} required /></div>
                    <div className="field" style={{ maxWidth: 200 }}><label>{T.brand}</label>
                        <select value={brand} onChange={(e) => setBrand(e.target.value)}>
                            {BRANDS.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                        </select></div>
                </div>
                <div className="row" style={{ alignItems: 'flex-start' }}>
                    <div className="field"><label>{T.periodLabel} <InfoTip>{T.periodTipBefore}<b>{T.periodTipBold}</b>{T.periodTipAfter}</InfoTip></label>
                        <input type="text" value={periodLabel} placeholder={T.periodPlaceholder} onChange={(e) => setPeriodLabel(e.target.value)} /></div>
                </div>

                {taxDecision && editable && (
                    <div className="field" style={{ marginTop: -4 }}>
                        <div>
                            <span title={textOf(TAX_DECISION_REASONS, taxDecision.code)} aria-hidden
                                style={{ color: vatZeichen(decisionStand).farbe, fontWeight: 700 }}>{vatZeichen(decisionStand).zeichen}</span>{' '}
                            {textOf(TAX_DECISION_REASONS, taxDecision.code)}
                            {decided && <> → <strong>{taxDecision.reverseCharge ? T.reverseCharge : fill(T.withVat, { percent: taxDecision.taxPercent })}</strong></>}
                        </div>
                        {!decided && (
                            <div className="notice err" style={{ marginTop: 6 }}>{T.viesPending}</div>
                        )}
                        {rateContradiction && (
                            <div className="notice err" style={{ marginTop: 6 }}>
                                {T.rateContradiction}{' '}
                                <button type="button" className="link-btn" onClick={applyDecisionRate}>{T.applyRate}</button>
                            </div>
                        )}
                    </div>
                )}

                <h2 style={{ marginTop: 8 }}>{T.positions}</h2>
                <LineItemsEditor items={items} onChange={setItems} defaultTaxRate={decided ? taxDecision!.taxPercent : undefined} />
            </div>
            </fieldset>

            {/* 64b: the log lives here and nowhere else — not in the PDF, not in the e-invoice, not in the export. */}
            {id && textChanges.length > 0 && (
                <div className="panel">
                    <h2>{T.textChangesHead}</h2>
                    <p className="hint">{T.textChangesNote}</p>
                    {textChanges.map((c) => (
                        <div key={c._id} className="notice" style={{ marginTop: 8 }}>
                            <div>
                                <b>{fill(T.textChangeMeta, { date: new Date(c.at).toLocaleString('de-DE'), by: c.by, n: c.lineIndex + 1 })}</b>
                                {c.wasSent && T.textChangeSent}
                                {' · '}
                                <button type="button" className="link-btn" onClick={() => openTab({
                                    id: `invoice-text-change-${c._id}`,
                                    name: `${T.textChangeOldPdf} ${invoiceNumber ?? ''}`,
                                    component: 'file-viewer',
                                    config: { url: textChangePdfUrl(id, c._id), fname: `${invoiceNumber ?? id}-${T.textChangeOldPdf}.pdf` },
                                })}>{T.textChangeOldPdf}</button>
                            </div>
                            <div style={{ marginTop: 4 }}>{T.textChangeReason}: {c.reason}</div>
                            <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}><span className="muted">{T.textChangeFrom}:</span> {c.from}</div>
                            <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}><span className="muted">{T.textChangeTo}:</span> {c.to}</div>
                        </div>
                    ))}
                </div>
            )}
            </div>

            <ConfirmDialog
                open={!!textEdit}
                title={fill(T.lineTextTitle, { invoiceNumber: invoiceNumber ?? '' })}
                danger={status === 'sent'}
                confirmLabel={T.lineTextSave}
                busy={busy === 'line-text'}
                error={error}
                message={textEdit && (
                    <>
                        <span style={{ display: 'block' }}>{T.lineTextIntro}</span>
                        {status === 'sent' && (
                            <span style={{ display: 'block', marginTop: 8, color: 'var(--danger)' }}>{T.lineTextSentWarning}</span>
                        )}
                        {items.length > 1 && (
                            <label className="field" style={{ display: 'block', marginTop: 10 }}>
                                {T.lineTextPosition}
                                <select value={textEdit.lineIndex} style={{ display: 'block', marginTop: 4 }}
                                    onChange={(e) => { const i = Number(e.target.value); setTextEdit({ ...textEdit, lineIndex: i, text: items[i]?.description ?? '' }) }}>
                                    {items.map((_, i) => <option key={i} value={i}>{fill(T.lineTextPositionOption, { n: i + 1 })}</option>)}
                                </select>
                            </label>
                        )}
                        <label className="field" style={{ display: 'block', marginTop: 10 }}>
                            {T.lineTextNew}
                            <textarea required rows={6} value={textEdit.text} style={{ display: 'block', marginTop: 4, width: '100%' }}
                                onChange={(e) => setTextEdit({ ...textEdit, text: e.target.value })} />
                        </label>
                        <label className="field" style={{ display: 'block', marginTop: 10 }}>
                            {T.lineTextReason}
                            <textarea required rows={2} value={textEdit.reason} style={{ display: 'block', marginTop: 4, width: '100%' }}
                                onChange={(e) => setTextEdit({ ...textEdit, reason: e.target.value })} />
                        </label>
                    </>
                )}
                onConfirm={doChangeText}
                onCancel={() => { if (busy !== 'line-text') { setTextEdit(null); setError('') } }}
            />

            <ConfirmDialog
                open={confirmFinalize}
                title={T.finalizeTitle}
                message={<>
                    <span style={{ display: 'block' }}>
                        {T.finalizeMessage}
                    </span>
                    {/* The server refuses this anyway — said here so the refusal does not come
                        as a surprise after the click. The document can only show one rate. */}
                    {mixedRates.length > 1 && (
                        <span style={{ display: 'block', marginTop: 8, color: 'var(--danger)' }}>
                            {fill(T.mixedRates, { rates: mixedRates.join(T.mixedRatesJoin) })}
                        </span>
                    )}
                    {taxDecision && (!decided || rateContradiction) && (
                        <span style={{ display: 'block', marginTop: 8, color: 'var(--danger)' }}>
                            {!decided ? TAX_DECISION_REASONS.unreachable : T.ratesVsReverseCharge}{' '}
                            {T.finalizeRefused}
                        </span>
                    )}
                </>}
                confirmLabel={T.finalize}
                busy={busy === 'finalize'}
                error={error}
                onConfirm={doFinalize}
                onCancel={() => { if (busy !== 'finalize') setConfirmFinalize(false) }}
            />

            {id && (
                <SendMailDrawer
                    open={sendOpen}
                    title={fill(T.sendDrawerTitle, { action: status === 'sent' ? T.resend : T.send, invoiceNumber: invoiceNumber ?? '' })}
                    load={() => getSendPreview(id)}
                    onSend={doSend}
                    onClose={() => setSendOpen(false)}
                />
            )}

            <ConfirmDialog
                open={confirmCancel}
                title={T.cancelTitle}
                message={<>
                    {fill(T.cancelMessage, { invoiceNumber: invoiceNumber ?? '' })}
                </>}
                confirmLabel={T.cancel}
                busy={busy === 'cancel'}
                error={error}
                onConfirm={doCancel}
                onCancel={() => { if (busy !== 'cancel') setConfirmCancel(false) }}
            />

            {/* Refund. Everything is phrasing content because ConfirmDialog renders the
                message inside a <p>. */}
            <ConfirmDialog
                open={refundOpen}
                title={fill(T.refundTitle, { invoiceNumber: invoiceNumber ?? '' })}
                danger
                confirmLabel={fill(T.refundConfirm, { amount: payoutNow.toFixed(2), sign })}
                busy={busy === 'refund'}
                error={error}
                message={refund && (
                    <>
                        <span style={{ display: 'block', marginBottom: 8 }}>
                            {T.refundIntro}
                        </span>
                        <span style={{ display: 'block' }}>{T.refundGross}<b>{refund.gross.toFixed(2)} {sign}</b></span>
                        {refund.alreadyRefunded > 0 && (
                            <span style={{ display: 'block' }}>
                                {fill(T.refundAlreadyPart, { amount: refund.alreadyRefunded.toFixed(2), sign })}<b>{refund.refundable.toFixed(2)} {sign}</b>
                            </span>
                        )}
                        <label style={{ display: 'block', marginTop: 10 }}>
                            {T.reason}
                            <select value={refundReason} onChange={(e) => setRefundReason(e.target.value as RefundReason)}
                                style={{ display: 'block', marginTop: 4, maxWidth: 320 }}>
                                <option value="cancellation">{T.reasonCancellation}</option>
                                <option value="withdrawal">{T.reasonWithdrawal}</option>
                            </select>
                        </label>
                        {refund.warning && (
                            <span style={{ display: 'block', marginTop: 10, color: 'var(--danger)' }}>{refund.warning}</span>
                        )}
                        <label style={{ display: 'block', marginTop: 10 }}>
                            {fill(T.feeLabel, { sign })}
                            <input type="number" step="0.01" min="0" max={refund.refundable}
                                value={isWithdrawal ? '0.00' : feeInput} disabled={isWithdrawal}
                                onChange={(e) => setFeeInput(e.target.value)}
                                style={{ display: 'block', marginTop: 4, maxWidth: 160 }} />
                        </label>
                        <span className="hint" style={{ display: 'block', marginTop: 4 }}>
                            {isWithdrawal
                                ? T.withdrawalNoFee
                                : <>
                                    {refund.fee.source === 'item'
                                        ? fill(T.feeFromItem, { itemId: refund.fee.itemId, label: refund.fee.label ? fill(T.feeFromItemLabel, { label: refund.fee.label }) : '' })
                                        : refund.fee.source === 'brand'
                                            ? T.feeFromBrand
                                            : T.feeNone}
                                    {' '}{T.feeAdjustable}
                                </>}
                        </span>
                        <span style={{ display: 'block', marginTop: 10 }}>
                            {T.payout}<b>{payoutNow.toFixed(2)} {sign}</b>
                        </span>
                        <span className="hint" style={{ display: 'block', marginTop: 4 }}>
                            {T.roundingHint}
                        </span>
                    </>
                )}
                onConfirm={doRefund}
                onCancel={() => { if (busy !== 'refund') { setRefundOpen(false); setError('') } }}
            />
            {refundDone && <div className="notice">{refundDone}</div>}
        </form>
    )
}
